import {ChangeDetectionStrategy, Component, effect, inject, NgZone, signal} from '@angular/core';
import {SearchWidgetData} from 'src/app/types/SearchWidgetData';
import {GtmEventEnum} from 'src/app/enums/GoogleEnums';
import {EventActionEnum, EventCategoryEnum} from 'src/app/enums/GaTrackingEnums';
import {GtmService} from 'src/app/services/gtm.service';
import {AppConfigService} from '../../../../services/app-config.service';
import {SearchService} from '../../../../services/search/search.service';
import {SearchWidgetFilterMenuItem} from '../../typings/search-widget-filter-menu-item';
import {SearchWidgetFiltersPageEnum} from '../../enums/search-widget-filters-page.enum';
import {SearchWidgetDataItem} from '../../../../types/SearchWidgetDataItem';
import {SearchWidgetDataItemGroupedFlat} from '../../../../types/SearchWidgetDataItemGroupedFlat';
import {QueryParams} from '../../../../types/QueryParams';
import {SearchReloadService} from '../../../../services/search/search-reload.service';
import {ToolsService} from '../../../../services/tools.service';

enum AnimationClass {
  CLOSED = 'closed',
  NORMAL = 'normal',
}

@Component({
  selector: 'app-search-categories-mobile-panel',
  templateUrl: './search-categories-mobile-panel.component.html',
  styleUrls: ['./search-categories-mobile-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchCategoriesMobilePanelComponent {
  protected isWidgetVisible = signal<boolean>(false);
  protected animationClass = signal<AnimationClass>(AnimationClass.CLOSED);
  protected widgetData = signal<SearchWidgetData>(null);
  protected menuItems = signal<SearchWidgetFilterMenuItem[]>([]);
  protected currentPage = signal<SearchWidgetFiltersPageEnum>(SearchWidgetFiltersPageEnum.MAIN);
  protected selectedCareServiceUnitGroup = signal<string>(null);
  protected careServiceUnitGroups = signal<SearchWidgetDataItemGroupedFlat[]>([]);
  protected careServiceUnits = signal<SearchWidgetDataItem[]>([]);
  protected selectedCareServiceTypeGroup = signal<string>(null);
  protected careServiceTypeGroups = signal<SearchWidgetDataItemGroupedFlat[]>([]);
  protected careServiceTypes = signal<SearchWidgetDataItem[]>([]);
  protected selectedSymptomsGroup = signal<string>(null);
  protected symptomsGroups = signal<SearchWidgetDataItemGroupedFlat[]>([]);
  protected symptoms = signal<SearchWidgetDataItem[]>([]);
  protected navigation = signal<SearchWidgetFiltersPageEnum[]>([]);
  private searchService = inject(SearchService);
  public mobileCategoriesPanelOpen = this.searchService.mobileCategoriesPanelOpen;
  private handledWidgetData = this.searchService.searchWidgetData;

  constructor(
    private gtmService: GtmService,
    public appConfigService: AppConfigService,
    private ngZone: NgZone,
    private reloadService: SearchReloadService,
    private tools: ToolsService,
  ) {
    this.menuItems.set([
      {
        label: 'Vårdgivare',
        target: SearchWidgetFiltersPageEnum.CARE_SERVICE_UNIT_GROUPS,
      },
    ]);
    if (this.appConfigService.appConfig.features.enableSymptomsInMegaMenu) {
      this.menuItems.update((items) => [
        ...items,
        {
          label: 'Symptom',
          target: SearchWidgetFiltersPageEnum.SYMPTOMS_GROUPS,
        },
      ]);
    }
    if (this.appConfigService.appConfig.features.enableCareServicesInMegaMenu) {
      this.menuItems.update((items) => [
        ...items,
        {
          label: 'Behandling',
          target: SearchWidgetFiltersPageEnum.CARE_SERVICE_TYPE_GROUPS,
        },
      ]);
    }
    this.navigation.set([]);
    this.currentPage.set(SearchWidgetFiltersPageEnum.MAIN);

    effect(
      () => {
        this.handleMobileCategoriesPanelOpen(this.mobileCategoriesPanelOpen());
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (this.handledWidgetData() != null) {
          this.widgetData.set(this.searchService.updateWidgetDataWithRouterLinks(this.handledWidgetData()));
        }
      },
      {allowSignalWrites: true},
    );
  }

  public onItemClick(routerLink: string[], queryParams: QueryParams): void {
    this.reloadService.reload(routerLink, queryParams);
    this.onCloseClick();
  }

  public onCloseClick(): void {
    this.closeThisPanel();
  }

  protected selectMenuItem(index: number) {
    const nextPage = this.menuItems()[index].target;
    this.navigateForward(this.currentPage(), nextPage);
  }

  protected selectCareServiceUnitGroup(index: number) {
    this.careServiceUnits.set(this.careServiceUnitGroups()[index].value);
    this.selectedCareServiceUnitGroup.set(this.careServiceUnitGroups()[index].key);

    this.navigateForward(this.currentPage(), SearchWidgetFiltersPageEnum.CARE_SERVICE_UNITS);
  }

  protected onCareServiceUnitClick(routerLink: string[], queryParams: QueryParams) {
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.SEARCHNAV,
      Action: EventActionEnum.CARE_SERVICE_CLICK,
      Label: 'click',
      Value: 1,
    };

    this.gtmService.pushTag(gtmTag);
    this.reloadService.reload(routerLink, queryParams);
    this.closeThisPanel();
  }

  protected selectCareServiceTypeGroup(index: number) {
    this.careServiceTypes.set(this.careServiceTypeGroups()[index].value);
    this.selectedCareServiceTypeGroup.set(this.careServiceTypeGroups()[index].key);
    this.navigateForward(this.currentPage(), SearchWidgetFiltersPageEnum.CARE_SERVICE_TYPES);
  }

  protected onCareServiceTypeClick(routerLink: string[], queryParams: QueryParams) {
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.SEARCHNAV,
      Action: EventActionEnum.CARE_SERVICE_TYPE_CLICK,
      Label: 'click',
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);
    this.reloadService.reload(routerLink, queryParams);
    this.closeThisPanel();
  }

  protected selectSymptomsGroup(index: number) {
    this.symptoms.set(this.symptomsGroups()[index].value);
    this.selectedSymptomsGroup.set(this.symptomsGroups()[index].key);
    this.navigateForward(this.currentPage(), SearchWidgetFiltersPageEnum.SYMPTOMS);
  }

  protected onSymptomClick(routerLink: string[], queryParams: QueryParams) {
    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.SEARCHNAV,
      Action: EventActionEnum.SYMPTOM_CLICK,
      Label: 'click',
      Value: 1,
    };

    this.gtmService.pushTag(gtmTag);
    this.reloadService.reload(routerLink, queryParams);
    this.closeThisPanel();
  }

  protected navigateBack() {
    this.currentPage.set(this.tools.clone(this.navigation()[this.navigation().length - 1]));
    this.navigation.update((items) => items.slice(0, -1));
  }

  private closeThisPanel(): void {
    this.searchService.mobileCategoriesPanelOpen.set(false);
  }

  private handleOpenPanel(result: boolean): void {
    if (result === false) {
      this.animationClass.set(AnimationClass.CLOSED);
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.ngZone.run(() => {
            this.isWidgetVisible.set(false);
          });
        }, 500);
      });
    } else {
      this.isWidgetVisible.set(true);
      this.animationClass.set(AnimationClass.CLOSED);
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.ngZone.run(() => {
            this.animationClass.set(AnimationClass.NORMAL);
          });
        }, 50);
      });
    }
  }

  private handleMobileCategoriesPanelOpen(result: boolean): void {
    this.handleOpenPanel(result);
    if (result) {
      this.updateWidgetDataWithNecessaryFields();
    }
  }

  private navigateForward(currentPage: SearchWidgetFiltersPageEnum, nextPage: SearchWidgetFiltersPageEnum) {
    this.navigation.update((items) => [...items, currentPage]);
    this.currentPage.set(nextPage);
  }

  private updateWidgetDataWithNecessaryFields(): void {
    let data: SearchWidgetData = this.searchService.updateWidgetDataWithRouterLinks(this.handledWidgetData());

    if (this.tools.isNil(data)) {
      return;
    }

    if (!this.tools.isNil(data.caregiverUnitTypesGrouped)) {
      this.careServiceUnitGroups.set(
        Object.keys(data.caregiverUnitTypesGrouped)
          .sort()
          .map((key) => ({
            key: key,
            value: data.caregiverUnitTypesGrouped[key],
          })),
      );
    }

    if (!this.tools.isNil(data.careServiceTypesGrouped)) {
      this.careServiceTypeGroups.set(
        Object.keys(data.careServiceTypesGrouped).map((key) => ({
          key: key,
          value: data.careServiceTypesGrouped[key],
        })),
      );
    }

    if (!this.tools.isNil(data.symptomsGrouped)) {
      this.symptomsGroups.set(
        Object.keys(data.symptomsGrouped).map((key) => ({
          key: key,
          value: data.symptomsGrouped[key],
        })),
      );
    }

    this.widgetData.set(data);
  }
}
